// ⌘K command palette — a fuzzy quick-switcher over every staff destination,
// permissioned ideation maps, vault documents, and Working Document workspaces.
//
// Direction: ADR-0078 (Bourse 2.0 IA composition). Destinations come from
// `nav-config.ts` (same inventory as the staff rail, the hidden sidebar,
// and MobileNav). Jump to… stays available on every viewport.
import { roleCanAccess, ROLES, STAFF_TENANT_CHANGE_EVENT } from "@l30/shared";
import { useIdeationMaps, useIdeationMapNodes } from "@l30/web-atlas/api/ideation-maps";
import { useDocuments } from "@l30/web-documents/api/documents";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { Search } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useCurrentRole } from "../../api/auth.js";
import { useFeatureFlags } from "../../api/feature-flags.js";
import { useInsightsSearch } from "../../api/insights-search.js";
import { useWorkspaces } from "../../api/workspaces.js";
import {
  insightsAskInsightsDestination,
  type InsightsSearchResult,
} from "../../lib/insights-search.js";
import { startSubjectTrail } from "../../lib/subject-trail.js";
import { useOptionalCapture } from "../capture/capture-provider.js";
import {
  filterNavSectionsForFlags,
  filterNavSectionsForRole,
  navItemHint,
} from "../layout/nav-config.js";
import { InsightsSearchResults } from "../search/insights-search-results.js";

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Open/close the ⌘K palette from anywhere inside the authenticated shell.
 */
export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error("useCommandPalette must be used within a CommandPaletteProvider");
  }
  return ctx;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [tenantEpoch, setTenantEpoch] = useState(0);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const reset = () => {
      setOpen(false);
      setTenantEpoch((n) => n + 1);
    };
    window.addEventListener(STAFF_TENANT_CHANGE_EVENT, reset);
    return () => window.removeEventListener(STAFF_TENANT_CHANGE_EVENT, reset);
  }, []);

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen, toggle }}>
      {children}
      <CommandPalette key={tenantEpoch} open={open} setOpen={setOpen} />
    </CommandPaletteContext.Provider>
  );
}

type GoOptions = {
  search?: Record<string, string | boolean | undefined>;
  params?: Record<string, string>;
};

/* eslint-disable-next-line max-lines-per-function, complexity -- destination inventory */
function CommandPalette({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const navigate = useNavigate();
  const role = useCurrentRole();
  const flagsQuery = useFeatureFlags();
  const canIdeation = roleCanAccess("ideation", role);
  const canDocuments = roleCanAccess("documents", role);
  const workspacesQuery = useWorkspaces({ enabled: open });
  const mapsQuery = useIdeationMaps({ enabled: open && canIdeation });
  const nodesQuery = useIdeationMapNodes({ enabled: open && canIdeation });
  const [search, setSearch] = useState("");
  const searching = search.trim().length > 0;
  const federated = useInsightsSearch(search, { enabled: open && searching });
  const documentsQuery = useDocuments({ limit: 40 }, undefined, {
    enabled: open && canDocuments && !searching,
  });
  const canCreateWorkspace = role === ROLES.ADMIN || role === ROLES.EXEC;
  const canOnboardPartner = role === ROLES.ADMIN;
  const canInvitePortalUser = roleCanAccess("portal-admin", role);
  const capture = useOptionalCapture();
  const enabledFlags = useMemo(() => {
    const set = new Set<string>();
    for (const f of flagsQuery.data?.flags ?? []) {
      if (f.enabled) set.add(f.key);
    }
    return set;
  }, [flagsQuery.data]);
  const sections = useMemo(
    () => filterNavSectionsForFlags(filterNavSectionsForRole(role), enabledFlags),
    [role, enabledFlags],
  );
  const workspaces = workspacesQuery.data?.workspaces ?? [];
  const maps = mapsQuery.data?.maps ?? [];
  const subjectNodes = nodesQuery.data?.nodes ?? [];
  const vaultDocuments = documentsQuery.data?.documents ?? [];
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      inputRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, [setOpen]);

  const go = useCallback(
    (to: string, options?: GoOptions) => {
      close();
      void navigate({
        to,
        ...(options?.params ? { params: options.params } : {}),
        ...(options?.search ? { search: options.search } : {}),
      });
    },
    [close, navigate],
  );

  const selectResult = useCallback(
    (result: InsightsSearchResult) => {
      if (result.type === "subject") startSubjectTrail();
      go(result.destination.to, {
        params: result.destination.params,
        search: result.destination.search,
      });
    },
    [go],
  );

  return (
    <dialog
      ref={dialogRef}
      aria-label="Command palette"
      onClose={close}
      className="dialog-motion fixed left-1/2 top-[15vh] m-0 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-soft-lg border bg-surface p-0 shadow-modal backdrop:bg-black/50"
    >
      <Command shouldFilter={!searching} label="Command palette" className="flex flex-col">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
          <Command.Input
            ref={inputRef}
            value={search}
            onValueChange={setSearch}
            placeholder="Search subjects, documents, people, or commands…"
            className="w-full bg-transparent py-3 text-sm text-fg outline-none placeholder:text-fg-subtle"
          />
        </div>
        <Command.List className="max-h-[min(24rem,60vh)] overflow-y-auto overscroll-contain p-2 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:pt-3 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wide **:[[cmdk-group-heading]]:text-fg-subtle">
          {searching ? (
            <InsightsSearchResults
              variant="command"
              grouped={federated.grouped}
              query={search.trim()}
              canAskInsights={federated.canAskInsights}
              emptyLabel="No matches. Try a subject, document, person, or command."
              onSelect={selectResult}
              onAskInsights={() =>
                go("/ai", { search: insightsAskInsightsDestination(search).search })
              }
            />
          ) : null}
          {!searching && canIdeation && subjectNodes.length > 0 ? (
            <Command.Group heading="Subjects">
              {subjectNodes
                .filter((node) => node.status === "active")
                .slice(0, 40)
                .map((node) => (
                  <Command.Item
                    key={node.id}
                    value={`subject ${node.label} ${node.kind} ${node.aliases.join(" ")}`}
                    aria-label={`${node.label}, Subject`}
                    data-search-type="subject"
                    onSelect={() => {
                      startSubjectTrail();
                      go("/subject/$subjectId", { params: { subjectId: node.id }, search: {} });
                    }}
                    className="flex cursor-pointer items-center justify-between gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                  >
                    <span className="truncate">{node.label}</span>
                    <span className="shrink-0 text-xs text-fg-subtle">{node.kind}</span>
                  </Command.Item>
                ))}
            </Command.Group>
          ) : null}
          {!searching && canIdeation && maps.length > 0 ? (
            <Command.Group heading="Maps">
              {maps.map((map) => (
                <Command.Item
                  key={map.id}
                  value={`map ${map.title} ${map.kind} ${map.visibility} atlas ideation`}
                  onSelect={() => go("/ideation/maps/$mapId", { params: { mapId: map.id } })}
                  className="flex cursor-pointer items-center justify-between gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  <span className="truncate">{map.title}</span>
                  <span className="shrink-0 text-xs text-fg-subtle">
                    {map.visibility === "private" ? "Private" : map.kind}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}
          {!searching && canDocuments && vaultDocuments.length > 0 ? (
            <Command.Group heading="Documents">
              {vaultDocuments.map((doc) => (
                <Command.Item
                  key={doc.id}
                  value={`document vault ${doc.title} ${doc.filename}`}
                  onSelect={() =>
                    go("/documents", {
                      search: { folderId: doc.folderId, q: doc.title },
                    })
                  }
                  className="flex cursor-pointer items-center justify-between gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  <span className="truncate">{doc.title}</span>
                  <span className="shrink-0 text-xs text-fg-subtle">Vault</span>
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}
          {!searching && capture ? (
            <Command.Group heading="Capture">
              <Command.Item
                value="capture fact question decision note"
                onSelect={() => {
                  close();
                  capture.open();
                }}
                className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
              >
                Capture…
              </Command.Item>
            </Command.Group>
          ) : null}
          {!searching ? (
            <Command.Group heading="Workspaces">
              <Command.Item
                value="workspaces working documents hub"
                onSelect={() => go("/documents", { search: { mode: "working" } })}
                className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
              >
                Working Documents
              </Command.Item>
              <Command.Item
                value="my vault personal workspace"
                onSelect={() => go("/my-vault")}
                className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
              >
                My vault
              </Command.Item>
              {canCreateWorkspace && (
                <Command.Item
                  value="create workspace new"
                  onSelect={() => go("/documents", { search: { mode: "working", create: true } })}
                  className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  Create workspace…
                </Command.Item>
              )}
              {canOnboardPartner && (
                <Command.Item
                  value="new partner portal onboard onboarding provision belsize wizard"
                  onSelect={() => go("/portal-users/new")}
                  className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  New partner portal…
                </Command.Item>
              )}
              {canInvitePortalUser && (
                <Command.Item
                  value="invite portal user add guest magic link supplier"
                  onSelect={() => go("/portal-users", { search: { invite: true } })}
                  className="flex cursor-pointer items-center gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  Invite portal user…
                </Command.Item>
              )}
              {workspaces.map((ws) => (
                <Command.Item
                  key={ws.id}
                  value={`workspace ${ws.name} ${ws.kind} ${ws.isPersonal ? "personal" : "shared"}`}
                  onSelect={() =>
                    ws.isPersonal
                      ? go("/my-vault", { search: { workspaceId: ws.id } })
                      : go("/documents", {
                          search: { mode: "working", workspaceId: ws.id },
                        })
                  }
                  className="flex cursor-pointer items-center justify-between gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                >
                  <span className="truncate">{ws.name}</span>
                  <span className="shrink-0 text-xs text-fg-subtle">
                    {ws.isPersonal ? "Personal" : ws.kind}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}
          {!searching
            ? sections.map((section) => (
                <Command.Group key={section.id} heading={section.label}>
                  {section.items.map((item) => {
                    const hint = navItemHint(item);
                    return (
                      <Command.Item
                        key={item.id}
                        value={[
                          section.label,
                          item.label,
                          item.href,
                          ...(item.keywords ?? []),
                        ].join(" ")}
                        onSelect={() => go(item.href)}
                        className="flex cursor-pointer flex-col items-start gap-0.5 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted"
                      >
                        <span>{item.label}</span>
                        {hint ? <span className="text-xs text-fg-subtle">{hint}</span> : null}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ))
            : null}
        </Command.List>
      </Command>
    </dialog>
  );
}
